import { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

interface UseSpeechRecognitionOptions {
  onResult?: (transcript: string) => void;
  onError?: (error: string) => void;
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
}

interface UseSpeechRecognitionReturn {
  isRecording: boolean;
  isProcessing: boolean;
  isSupported: boolean;
  startRecording: () => void;
  stopRecording: () => void;
  toggleRecording: () => void;
}

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {}
): UseSpeechRecognitionReturn {
  const {
    onResult,
    onError,
    lang = 'en-US',
    continuous = false,
    interimResults = false,
  } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSupported, setIsSupported] = useState(() => {
    // Check support on mount (sync, no effect needed)
    if (typeof window === 'undefined') return false;
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    return !!SpeechRecognition;
  });
  const recognitionRef = useRef<any>(null);
  const isInitializedRef = useRef(false);
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);

  // Keep refs up to date without triggering re-initialization
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Lazy initialization - only when user clicks microphone button
  const initializeRecognition = useCallback(() => {
    if (isInitializedRef.current || recognitionRef.current) {
      return; // Already initialized
    }

    if (typeof window === 'undefined') return;

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('❌ Speech Recognition not supported');
      return;
    }

    console.log('🎤 Initializing Speech Recognition...');

    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = continuous;
    recognitionRef.current.interimResults = interimResults;
    recognitionRef.current.lang = lang;

    // Event: Recording started
    recognitionRef.current.onstart = () => {
      console.log('🎤 Recording started');
      setIsRecording(true);
    };

    // Event: Speech recognized
    recognitionRef.current.onresult = (event: any) => {
      console.log('🎤 Speech recognized:', event.results);
      const transcript = event.results[0][0].transcript;
      console.log('🎤 Transcript:', transcript);

      if (onResultRef.current) {
        onResultRef.current(transcript);
      }

      setIsRecording(false);
      setIsProcessing(false);
    };

    // Event: Error occurred
    recognitionRef.current.onerror = (event: any) => {
      console.error('🎤 Speech recognition error:', event.error);
      setIsRecording(false);
      setIsProcessing(false);

      if (event.error === 'no-speech') {
        // No toast - dialog provides feedback
        if (onErrorRef.current) {
          onErrorRef.current('no-speech');
        }
      } else if (event.error === 'not-allowed') {
        toast.error(
          'Microphone access denied. Please enable it in browser settings.'
        );
        if (onErrorRef.current) {
          onErrorRef.current('not-allowed');
        }
      } else {
        toast.error(`Voice recognition failed: ${event.error}`);
        if (onErrorRef.current) {
          onErrorRef.current(event.error);
        }
      }
    };

    // Event: Recording ended
    recognitionRef.current.onend = () => {
      console.log('🎤 Recording ended');
      setIsRecording(false);
      setIsProcessing(false);
    };

    isInitializedRef.current = true;
    console.log('✅ Speech Recognition initialized successfully');
  }, [lang, continuous, interimResults]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, []);

  // Start recording
  const startRecording = useCallback(() => {
    // Lazy initialize on first use
    if (!isInitializedRef.current) {
      initializeRecognition();
    }

    if (!recognitionRef.current) {
      console.error('❌ Recognition not initialized');
      toast.error(
        'Voice input is not supported in your browser. Please try Chrome or Edge.'
      );
      return;
    }

    try {
      console.log('🎤 Starting recording...');
      recognitionRef.current.start();
    } catch (error) {
      console.error('❌ Failed to start voice recording:', error);
      setIsRecording(false);
      toast.error('Failed to start voice recording. Please try again.');
    }
  }, [initializeRecognition]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (!recognitionRef.current) return;

    console.log('🎤 Stopping recording...');
    recognitionRef.current.stop();
    setIsRecording(false);
    setIsProcessing(false);
  }, []);

  // Toggle recording
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    isProcessing,
    isSupported,
    startRecording,
    stopRecording,
    toggleRecording,
  };
}
